import { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';

const useExportPDF = () => {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const exportToPDF = useCallback(async (elementId, options = {}) => {
    const {
      filename = `dashboard-${format(new Date(), 'yyyy-MM-dd-HHmm')}`,
      title = 'ERP Dashboard Report',
      orientation = 'landscape',
      format: pageFormat = 'a4',
      scale = 2,
      margin = 10,
      includeHeader = true,
      includeFooter = true,
      companyName = 'AMAL ERP',
      onProgress = null
    } = options;

    setExporting(true);
    setProgress(0);
    setError(null);

    try {
      const element = document.getElementById(elementId);

      if (!element) {
        throw new Error(`Element with ID "${elementId}" not found`);
      }

      // Update progress
      const updateProgress = (value) => {
        setProgress(value);
        onProgress?.(value);
      };

      updateProgress(10);

      // Capture the element as canvas
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          // Optionally modify cloned document before capture
          const clonedElement = clonedDoc.getElementById(elementId);
          if (clonedElement) {
            // Remove any elements with class 'no-print'
            const noPrintElements = clonedElement.querySelectorAll('.no-print');
            noPrintElements.forEach(el => el.remove());
          }
        }
      });

      updateProgress(50);

      // Calculate dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Create PDF
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: pageFormat
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Calculate scaling to fit page
      const effectiveWidth = pageWidth - (margin * 2);
      const effectiveHeight = pageHeight - (margin * 2) - (includeHeader ? 15 : 0) - (includeFooter ? 10 : 0);

      const widthRatio = effectiveWidth / imgWidth;
      const heightRatio = effectiveHeight / imgHeight;
      const ratio = Math.min(widthRatio, heightRatio);

      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      // Center the image
      const xOffset = margin + (effectiveWidth - scaledWidth) / 2;
      const yOffset = margin + (includeHeader ? 15 : 0);

      updateProgress(70);

      // Add header if enabled
      if (includeHeader) {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(128, 0, 32); // Maroon color
        pdf.text(title, margin, margin + 8);

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          `Generated on ${format(new Date(), 'dd MMM yyyy, HH:mm')}`,
          pageWidth - margin - 50,
          margin + 8
        );

        // Add separator line
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(margin, margin + 12, pageWidth - margin, margin + 12);
      }

      // Add the dashboard image
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);

      updateProgress(90);

      // Add footer if enabled
      if (includeFooter) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(150, 150, 150);

        const footerY = pageHeight - margin;
        pdf.text(companyName, margin, footerY);
        pdf.text(
          `Page 1 of 1`,
          pageWidth - margin - 20,
          footerY
        );
      }

      // Save the PDF
      pdf.save(`${filename}.pdf`);

      updateProgress(100);

      return { success: true, filename: `${filename}.pdf` };
    } catch (err) {
      console.error('PDF export error:', err);
      setError(err.message || 'Failed to export PDF');
      return { success: false, error: err.message };
    } finally {
      setExporting(false);
    }
  }, []);

  // Export specific widget to PDF
  const exportWidgetToPDF = useCallback(async (elementId, widgetName, options = {}) => {
    return exportToPDF(elementId, {
      ...options,
      filename: `${widgetName.toLowerCase().replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd-HHmm')}`,
      title: widgetName,
      orientation: 'portrait'
    });
  }, [exportToPDF]);

  // Export multiple elements to multi-page PDF
  const exportMultiPagePDF = useCallback(async (elementIds, options = {}) => {
    const {
      filename = `dashboard-full-${format(new Date(), 'yyyy-MM-dd-HHmm')}`,
      title = 'ERP Dashboard Full Report',
      orientation = 'landscape',
      format: pageFormat = 'a4'
    } = options;

    setExporting(true);
    setProgress(0);
    setError(null);

    try {
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: pageFormat
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;

      for (let i = 0; i < elementIds.length; i++) {
        const elementId = elementIds[i];
        const element = document.getElementById(elementId);

        if (!element) continue;

        // Add new page for subsequent elements
        if (i > 0) {
          pdf.addPage();
        }

        // Capture element
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Calculate scaling
        const effectiveWidth = pageWidth - (margin * 2);
        const effectiveHeight = pageHeight - (margin * 2) - 20;

        const ratio = Math.min(
          effectiveWidth / imgWidth,
          effectiveHeight / imgHeight
        );

        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;

        const xOffset = margin + (effectiveWidth - scaledWidth) / 2;

        // Add header
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(128, 0, 32);
        pdf.text(title, margin, margin + 8);

        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i + 1} of ${elementIds.length}`, pageWidth - margin - 25, margin + 8);

        // Add image
        pdf.addImage(imgData, 'PNG', xOffset, margin + 15, scaledWidth, scaledHeight);

        setProgress(((i + 1) / elementIds.length) * 100);
      }

      pdf.save(`${filename}.pdf`);

      return { success: true, filename: `${filename}.pdf` };
    } catch (err) {
      console.error('Multi-page PDF export error:', err);
      setError(err.message || 'Failed to export PDF');
      return { success: false, error: err.message };
    } finally {
      setExporting(false);
      setProgress(0);
    }
  }, []);

  // Reset state
  const reset = useCallback(() => {
    setExporting(false);
    setProgress(0);
    setError(null);
  }, []);

  return {
    exporting,
    progress,
    error,
    exportToPDF,
    exportWidgetToPDF,
    exportMultiPagePDF,
    reset
  };
};

export default useExportPDF;
